import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '../ui/Button';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { localToday } from '../../utils/date';

// Phase E — hands-free voice conversation (voice in, voice out, no tapping between turns).
// You talk, it notices you've stopped, ships that utterance to /api/health/voice-turn
// (Whisper STT -> deterministic run_turn -> optional TTS), speaks the reply, and listens
// again. The mic is muted while it thinks/speaks (half-duplex), so it never hears itself.
//
// Turn detection is a plain Web-Audio energy meter, NOT a neural model. We tried Silero
// (onnxruntime/WASM) but it dead-ended on the app's Content-Security-Policy: the WASM glue
// can only load cross-origin or with 'wasm-unsafe-eval', both of which need editing the
// nginx CSP header by hand — a risky change to a live site's security. An RMS energy
// detector needs none of that: same-origin getUserMedia + AudioContext + MediaRecorder,
// all already allowed, works in every browser incl. Brave. Less clever than Silero, but
// for talking to a logger in a normal room it's plenty, and WE own the silence threshold
// (the Phase D fix: a thinking pause must not get read as "done").
//
// Capture reuses the Phase D MediaRecorder path (proven on Curtis's phone): we start
// recording when energy rises and stop after a generous silence, then send that one clip.

const CATEGORY_EMOJI = {
  exercise: '🏃', nutrition: '🍽️', sleep: '😴', biometric: '📏', mental_health: '🧠',
  therapy: '🌿', medical: '🩺', environmental: '🌤️', social: '👥', other: '•',
};

const entryLabel = (e) => {
  const emoji = CATEGORY_EMOJI[(e.category_slug || '').split('.')[0]] || '•';
  const name = (e.entry_type || '').replace(/_/g, ' ');
  let detail = '';
  if (e.value != null) detail = ` ${e.value}${e.value_unit ? ' ' + e.value_unit : ''}`;
  else if (e.calories != null) detail = ` ${Math.round(e.calories)} cal`;
  else if (e.duration_minutes != null) detail = ` ${e.duration_minutes} min`;
  return `${emoji} ${name}${detail}`;
};

// Strip emoji/symbols before speaking — the reply carries category emoji (🏃 🍽️) for the
// on-screen bubble, but a TTS voice reads them literally ("knife and fork"). Displayed text
// keeps them; only the spoken version is cleaned.
const stripForSpeech = (t) => (t || '')
  .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}\u{200D}]/gu, '')
  .replace(/\s{2,}/g, ' ')
  .trim();

// Container the transcription API takes as-is; Safari/iOS only offers mp4.
const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

// --- Turn-detection tuning (WE own these — the Phase D lesson) --------------------------
const POLL_MS = 50;               // energy sampled 20x/sec
const SPEECH_RMS = 0.018;         // RMS above this = voice (quiet room ~0.002, speech ~0.05+)
const SILENCE_HANG_MS = 3000;     // silence this long after speech = end of turn. Curtis
                                  //   prefers fewer interruptions and is fine waiting 3s.
const MIN_UTTERANCE_MS = 350;     // shorter than this = a cough/clack, discard
const MAX_UTTERANCE_MS = 30000;   // force-close a monologue so it still uploads
// Opus is compressed (~2KB/s) so a 30s clip is ~60KB — nowhere near the 1MB nginx cap,
// but keep a guard rather than let anything 413 at the proxy.
const MAX_UPLOAD_BYTES = 1000 * 1024;

// Whether a real mic + recording can exist here (HTTPS/localhost + the APIs we use).
// Mirrors the typed logger's gate so the entry button self-hides on the LAN-HTTP dev box.
export const canConverse = typeof window !== 'undefined'
  && typeof navigator !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia
  && typeof MediaRecorder !== 'undefined'
  && !!(window.AudioContext || window.webkitAudioContext)
  && (window.isSecureContext ?? true);

const VoiceConversation = ({ onClose, onEntrySaved, defaultDate }) => {
  // connecting → listening ⇄ thinking → speaking → listening … | error
  const [status, setStatus] = useState('connecting');
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [errorText, setErrorText] = useState('');

  // Audio graph + recorder handles (refs — the poll loop and callbacks must see live values).
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const bufRef = useRef(null);
  const pollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef('');
  const recordingRef = useRef(false);       // a clip is being captured right now
  const speechStartRef = useRef(0);
  const lastVoiceRef = useRef(0);
  const activeRef = useRef(false);           // detecting? false while thinking/speaking (half-duplex)
  const busyRef = useRef(false);             // a turn is in flight
  const cancelledRef = useRef(false);
  const audioElRef = useRef(null);
  const wakeLockRef = useRef(null);          // keeps the phone screen awake during the call
  const messagesRef = useRef([]);            // mirror for callbacks (avoid stale closure)
  const dateRef = useRef(defaultDate || localToday());
  const handleUtteranceRef = useRef(null);
  const scrollRef = useRef(null);

  const pushMessage = useCallback((msg) => {
    setMessages((prev) => {
      const next = [...prev, msg];
      messagesRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, status]);

  // Resume detecting after a reply finishes (or fails).
  const resumeListening = useCallback(() => {
    audioElRef.current = null;
    busyRef.current = false;
    recordingRef.current = false;
    chunksRef.current = [];
    if (cancelledRef.current) return;
    setUserSpeaking(false);
    setStatus('listening');
    activeRef.current = true;   // energy poll starts acting again
  }, []);

  // Speak the reply: server TTS audio if present, else the free browser voice. Either way,
  // resume listening when it finishes.
  const speakReply = useCallback((reply, audioB64, audioMime) => {
    if (cancelledRef.current) return;
    setStatus('speaking');

    const spoken = stripForSpeech(reply);
    const browserFallback = () => {
      const synth = window.speechSynthesis;
      if (!synth || !spoken) { resumeListening(); return; }
      try {
        synth.cancel();
        const utter = new SpeechSynthesisUtterance(spoken);
        utter.onend = () => resumeListening();
        utter.onerror = () => resumeListening();
        synth.speak(utter);
      } catch (e) { resumeListening(); }
    };

    if (!audioB64) { browserFallback(); return; }
    try {
      const audio = new Audio(`data:${audioMime || 'audio/mpeg'};base64,${audioB64}`);
      audioElRef.current = audio;
      audio.onended = () => resumeListening();
      audio.onerror = () => browserFallback();
      const played = audio.play();
      if (played?.catch) played.catch(() => browserFallback());  // autoplay blocked → browser voice
    } catch (e) { browserFallback(); }
  }, [resumeListening]);

  // One captured utterance (a recorded clip) → send it through the voice turn.
  const handleUtterance = useCallback(async (blob) => {
    if (cancelledRef.current) return;
    if (blob.size > MAX_UPLOAD_BYTES) {
      toast('That was a long one — say it in a shorter breath so it sends.');
      resumeListening();
      return;
    }

    const history = messagesRef.current
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-12)
      .map((m) => ({ role: m.role, content: m.text }));

    try {
      const form = new FormData();
      const type = blob.type || 'audio/webm';
      const ext = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm';
      form.append('audio', blob, `utterance.${ext}`);
      form.append('history', JSON.stringify(history));
      form.append('date', dateRef.current);
      // apiClient defaults Content-Type to application/json; it MUST be cleared for a
      // FormData body so the browser sets multipart/form-data + boundary (else 422).
      const res = await apiClient.post('/api/health/voice-turn', form, {
        timeout: 180000,
        headers: { 'Content-Type': undefined },
      });
      if (cancelledRef.current) return;

      const { transcript, reply, entries, logged_count, audio, audio_mime } = res.data;
      if (transcript && transcript.trim()) pushMessage({ role: 'user', text: transcript.trim() });
      pushMessage({ role: 'assistant', text: reply, entries: entries || [] });
      if (logged_count > 0 && onEntrySaved) onEntrySaved();
      speakReply(reply, audio, audio_mime);
    } catch (error) {
      if (cancelledRef.current) return;
      const s = error.response?.status;
      const detail = error.response?.data?.detail;
      if (s === 429) toast.error(detail || 'Slow down a moment — too many turns.');
      else if (s === 401) toast.error('Please log in again to keep talking.');
      else if (s === 413) toast.error('That was too long to send. Try a shorter breath.');
      else if (s === 400) { /* usually silence — no words heard; keep listening */ }
      else toast.error(detail || "I didn't catch that. Try again.");
      resumeListening();
    }
  }, [pushMessage, onEntrySaved, resumeListening, speakReply]);

  handleUtteranceRef.current = handleUtterance;

  // Fired when a clip's recorder stops: decide misfire vs real utterance.
  const onClipStop = useCallback(() => {
    const durationMs = performance.now() - speechStartRef.current;
    const type = mimeRef.current || 'audio/webm';
    const blob = new Blob(chunksRef.current, { type });
    chunksRef.current = [];
    recordingRef.current = false;
    setUserSpeaking(false);
    if (cancelledRef.current) return;
    if (durationMs < MIN_UTTERANCE_MS || blob.size < 1200) {
      // Too short to be speech (a cough, a knock) — ignore and keep listening.
      activeRef.current = true;
      return;
    }
    busyRef.current = true;
    activeRef.current = false;   // half-duplex: stop detecting while we think + speak
    setStatus('thinking');
    handleUtteranceRef.current?.(blob);
  }, []);

  const startClip = useCallback(() => {
    if (recordingRef.current || cancelledRef.current || !streamRef.current) return;
    let recorder;
    try {
      recorder = new MediaRecorder(streamRef.current, mimeRef.current ? { mimeType: mimeRef.current } : undefined);
    } catch (e) { return; }
    chunksRef.current = [];
    recorder.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
    recorder.onstop = () => onClipStop();
    recorderRef.current = recorder;
    recordingRef.current = true;
    speechStartRef.current = performance.now();
    lastVoiceRef.current = speechStartRef.current;
    // Timeslice: force a data callback every second rather than relying only on the final
    // flush at stop() — mobile encoders are unreliable about that last chunk (Phase D), and
    // an empty clip would be silently discarded as a misfire with no reply.
    recorder.start(1000);
    setUserSpeaking(true);
  }, [onClipStop]);

  const stopClip = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') {
      try { r.requestData(); } catch (e) { /* not all engines allow it */ }
      try { r.stop(); } catch (e) { /* already stopping */ }
    }
  }, []);

  // The energy poll — runs every POLL_MS, but only acts while activeRef (listening) is on.
  const poll = useCallback(() => {
    if (cancelledRef.current || !activeRef.current || !analyserRef.current) return;
    const buf = bufRef.current;
    analyserRef.current.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    const now = performance.now();

    if (rms >= SPEECH_RMS) {
      lastVoiceRef.current = now;
      if (!recordingRef.current) startClip();
    }
    if (recordingRef.current) {
      const silentFor = now - lastVoiceRef.current;
      const total = now - speechStartRef.current;
      if (silentFor >= SILENCE_HANG_MS || total >= MAX_UTTERANCE_MS) stopClip();
    }
  }, [startClip, stopClip]);

  // Boot the mic + audio graph once, on mount.
  useEffect(() => {
    cancelledRef.current = false;
    let cleanupStream = null;

    // Keep the screen awake for the whole conversation — otherwise the phone sleeps
    // mid-sentence. The lock auto-releases when the tab is hidden, so re-acquire on
    // return-to-visible. Best-effort: unsupported/denied just means the screen may dim.
    const acquireWakeLock = async () => {
      try {
        if ('wakeLock' in navigator && !cancelledRef.current) {
          const sentinel = await navigator.wakeLock.request('screen');
          // Auto-releases when the tab hides; null the ref so onVisible re-acquires.
          sentinel.addEventListener('release', () => { wakeLockRef.current = null; });
          wakeLockRef.current = sentinel;
        }
      } catch (e) { /* not critical */ }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelledRef.current && !wakeLockRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    (async () => {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // autoGainControl OFF on purpose: it boosts gain during quiet stretches, lifting
          // the noise floor above the speech threshold so end-of-speech never registers and
          // the clip never sends. Keep echo/noise suppression (they only help).
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
        });
      } catch (e) {
        if (!cancelledRef.current) {
          setStatus('error');
          const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
          setErrorText(denied
            ? 'Microphone access was denied. Allow it in your browser to have a conversation.'
            : "Couldn't reach your microphone. You can use the typed logger instead.");
        }
        return;
      }
      if (cancelledRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }

      cleanupStream = stream;
      streamRef.current = stream;
      mimeRef.current = pickMimeType();
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        // iOS starts the context suspended until a gesture; the "Have a conversation" tap
        // that mounted us counts, so resume() here is within that gesture chain.
        if (ctx.state === 'suspended') { try { await ctx.resume(); } catch (e) { /* noop */ } }
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);   // analyser NOT connected to destination — no mic playback
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        bufRef.current = new Float32Array(analyser.fftSize);
      } catch (e) {
        if (!cancelledRef.current) {
          setStatus('error');
          setErrorText(`Couldn't start the audio engine: ${e?.message || 'unknown error'}`);
        }
        return;
      }

      activeRef.current = true;
      pollRef.current = setInterval(poll, POLL_MS);
      acquireWakeLock();
      setStatus('listening');
    })();

    return () => {
      cancelledRef.current = true;
      activeRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      if (wakeLockRef.current) { try { wakeLockRef.current.release(); } catch (e) { /* noop */ } wakeLockRef.current = null; }
      try { window.speechSynthesis?.cancel(); } catch (e) { /* noop */ }
      if (audioElRef.current) { try { audioElRef.current.pause(); } catch (e) { /* noop */ } audioElRef.current = null; }
      const r = recorderRef.current;
      if (r && r.state !== 'inactive') { try { r.stop(); } catch (e) { /* noop */ } }
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) { /* noop */ } }
      const s = streamRef.current || cleanupStream;
      if (s) s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // Boot once; poll/refs carry live state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusLine = () => {
    switch (status) {
      case 'connecting': return 'Warming up the microphone…';
      case 'listening': return userSpeaking ? 'Hearing you…' : 'Listening — just start talking.';
      case 'thinking': return 'Thinking…';
      case 'speaking': return 'Speaking…';
      case 'error': return errorText || 'Something went wrong.';
      default: return '';
    }
  };

  const orbState = status === 'error' ? 'error'
    : (status === 'listening' && userSpeaking) ? 'hearing'
    : status;

  return (
    <div className="flex h-full flex-col">
      {/* No title header — the mode toggle below already says "Auto". Just the transcript,
          then a compact status/End row, so the checklist above stays on one screen. */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && status !== 'error' && (
          <p className="text-center text-sm text-fg-2 mt-8">
            Say what you ate, how you moved, or how you slept. I'll log it and talk back —
            no need to tap between turns.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
              m.role === 'user'
                ? 'bg-cta text-cta-fg rounded-br-sm'
                : 'bg-elev text-fg rounded-bl-sm'
            }`}>
              {m.text}
              {m.entries && m.entries.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.entries.map((e, j) => (
                    <span key={j} className="inline-flex items-center rounded-full border border-line-strong px-2 py-0.5 text-sm font-medium text-fg-2">
                      {entryLabel(e)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Compact status bar — orb + one line + a small End, so the whole session stays
          small enough that the checklist above remains on screen and ticks green live. */}
      <div className="border-t border-line px-4 py-2.5 flex items-center gap-3">
        <VoiceOrb state={orbState} />
        <p className={`min-w-0 flex-1 text-sm font-medium ${
          status === 'error' ? 'text-off-strong dark:text-off' : 'text-fg-2'
        }`}>
          {statusLine()}
        </p>
        <Button variant="ghost" onClick={onClose} className="shrink-0 text-sm" title="End the conversation">End</Button>
      </div>
    </div>
  );
};

const VoiceOrb = ({ state }) => {
  if (state === 'hearing') {
    return (
      <span className="flex items-end gap-0.5 h-7 shrink-0" aria-label="hearing you">
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} className="w-1.5 rounded-full bg-fg animate-pulse"
            style={{ height: `${8 + (i % 2 === 0 ? 8 : 16)}px`, animationDelay: `${i * 90}ms` }} />
        ))}
      </span>
    );
  }
  if (state === 'thinking') {
    return (
      <span className="flex items-center gap-1 h-7 shrink-0" aria-label="thinking">
        {[0, 1, 2].map((i) => (
          <span key={i} className="w-2 h-2 rounded-full bg-fg-3 animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />
        ))}
      </span>
    );
  }
  const color = state === 'error' ? 'bg-red-500' : state === 'connecting' ? 'bg-fg-3' : 'bg-good-strong dark:bg-good';
  const ping = state === 'listening' || state === 'speaking' || state === 'connecting';
  return (
    <span className="relative flex h-7 w-7 items-center justify-center shrink-0" aria-label={state}>
      {ping && <span className={`absolute inline-flex h-7 w-7 rounded-full opacity-30 ${color} animate-ping`} />}
      <span className={`relative inline-flex h-5 w-5 rounded-full ${color}`} />
    </span>
  );
};

export default VoiceConversation;
